export type LeadReferrerCategory =
  "direct" | "internal" | "search" | "social" | "external";

export type LeadAttribution = {
  landingPath: string | null;
  campaignSource: string | null;
  campaignMedium: string | null;
  campaignName: string | null;
  referrerCategory: LeadReferrerCategory;
};

const searchReferrers = [
  "bing.com",
  "duckduckgo.com",
  "google.com",
  "google.pl",
  "yahoo.com",
];
const socialReferrers = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "t.co",
  "twitter.com",
  "x.com",
  "youtube.com",
];
const referrerCategories = new Set<LeadReferrerCategory>([
  "direct",
  "internal",
  "search",
  "social",
  "external",
]);

export function normalizeCampaignValue(value: string | null) {
  const normalized = value?.trim() ?? "";
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/.test(normalized)
    ? normalized
    : null;
}

export function categorizeReferrer(
  referrer: string,
  currentHostname: string,
): LeadReferrerCategory {
  if (!referrer) return "direct";
  try {
    const hostname = new URL(referrer).hostname.toLowerCase();
    const matches = (domain: string) =>
      hostname === domain || hostname.endsWith(`.${domain}`);
    if (hostname === currentHostname.toLowerCase()) return "internal";
    if (searchReferrers.some(matches)) return "search";
    if (socialReferrers.some(matches)) return "social";
  } catch {
    return "external";
  }
  return "external";
}

export function readLeadAttribution(input: {
  pathname: string;
  search: string;
  referrer: string;
  currentHostname: string;
}): LeadAttribution {
  const query = new URLSearchParams(input.search);
  const carriedReferrer = query.get("kt_ref") as LeadReferrerCategory | null;
  const carriedLandingPath = query.get("kt_entry");
  const validPath = (value: string | null) =>
    value !== null &&
    value.length <= 160 &&
    /^\/[A-Za-z0-9/_.-]*$/.test(value) &&
    !value.includes("..");
  return {
    landingPath: validPath(carriedLandingPath)
      ? carriedLandingPath
      : validPath(input.pathname)
        ? input.pathname
        : null,
    campaignSource: normalizeCampaignValue(query.get("utm_source")),
    campaignMedium: normalizeCampaignValue(query.get("utm_medium")),
    campaignName: normalizeCampaignValue(query.get("utm_campaign")),
    referrerCategory:
      carriedReferrer && referrerCategories.has(carriedReferrer)
        ? carriedReferrer
        : categorizeReferrer(input.referrer, input.currentHostname),
  };
}

export function addAttributionToInternalUrl(
  target: URL,
  currentOrigin: string,
  attribution: LeadAttribution,
) {
  if (target.origin !== currentOrigin || !/^https?:$/.test(target.protocol)) {
    return target;
  }
  const campaignParameters = {
    utm_source: attribution.campaignSource,
    utm_medium: attribution.campaignMedium,
    utm_campaign: attribution.campaignName,
  };
  for (const [name, value] of Object.entries(campaignParameters)) {
    if (value && !target.searchParams.has(name)) {
      target.searchParams.set(name, value);
    }
  }
  if (!target.searchParams.has("kt_ref")) {
    target.searchParams.set("kt_ref", attribution.referrerCategory);
  }
  if (attribution.landingPath && !target.searchParams.has("kt_entry")) {
    target.searchParams.set("kt_entry", attribution.landingPath);
  }
  return target;
}
